/**
 * DotPatternDisplay - Dot pattern display for subitizing assessments
 *
 * Shows a flash pattern of dots that students must count.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { VISUAL_CONFIG } from '../preKConstants';

interface Dot {
  x: number;
  y: number;
  id: string;
}

interface DotPatternDisplayProps {
  dots: Dot[];
  isVisible: boolean;
}

/**
 * Generate a random dot pattern for a given quantity
 */
export const generateDotPattern = (quantity: number): Dot[] => {
  // Shuffle grid positions and select the first 'quantity' positions
  const shuffledPositions = [...VISUAL_CONFIG.SUBITIZING_GRID].sort(
    () => Math.random() - 0.5
  );
  const selectedPositions = shuffledPositions.slice(0, quantity);

  return selectedPositions.map((pos, i) => ({
    x: pos.x,
    y: pos.y,
    id: `dot-${i}`,
  }));
};

const DotPatternDisplay: React.FC<DotPatternDisplayProps> = ({ dots, isVisible }) => {
  if (!isVisible) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 gap-8 lg:gap-12 relative">
      {/* Create invisible 4-card layout area for positioning */}
      <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
      <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
      <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
      <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>

      {/* Dots positioned absolutely over this area */}
      {dots.map((dot) => (
        <motion.div
          key={dot.id}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="absolute w-12 h-12 lg:w-16 lg:h-16 bg-blue-600 rounded-full shadow-lg"
          style={{
            left: `${dot.x}%`,
            top: `${dot.y}%`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}
    </div>
  );
};

export default DotPatternDisplay;
